import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ExternalLink, FileText } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { documentTone } from '@/lib/status';
import {
  useCredentialKindLabel,
  useDocumentStatusLabel,
  useModerationDocumentKindLabel,
} from '@/lib/labels';
import { useTranslations } from '@/i18n/use-locale';
import { DOCUMENT_STATUS, type ModerationDocument } from '@/contracts';
import { type ListReturnState } from '@/lib/detail';
import { formatBytes, formatDuration } from '@/lib/format';
import { useDecideModeration } from '@/features/verification/use-decide-moderation';
import { useVideoDecisionWording } from '@/features/verification/use-video-wording';
import { QueryGate } from '@/components/ui/query-gate';
import { useModerationQueueQuery } from '@/lib/queries';
import { ReasonConfirm, type ConfirmMode } from '@/features/verification/reason-confirm';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import { FilterChips } from '@/components/admin/filter-chips';
import { VideoPlayer } from '@/components/admin/video-player';
import { KIND_FILTERS, parseDocumentKind, type KindFilter } from './document-kind-filter';

export function DocumentsRoute() {
  const t = useTranslations('admin.documents');
  const credentialKindLabel = useCredentialKindLabel();
  const documentStatusLabel = useDocumentStatusLabel();
  const documentKindLabel = useModerationDocumentKindLabel();
  const videoWording = useVideoDecisionWording();
  // Which row has a confirmation open, and which one. Approval asks first too (#88): it
  // is granted for good.
  const [confirm, setConfirm] = useState<{ id: string; mode: ConfirmMode } | null>(null);
  const toggleConfirm = (id: string, mode: ConfirmMode) =>
    setConfirm((current) => (current?.id === id && current.mode === mode ? null : { id, mode }));
  const navigate = useNavigate();
  const location = useLocation();
  const returnState: ListReturnState = { from: `${location.pathname}${location.search}` };
  const [searchParams, setSearchParams] = useSearchParams();
  const kindFilter = parseDocumentKind(searchParams.get('documentKind'));
  const setKindFilter = (next: KindFilter) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'all') params.delete('documentKind');
        else params.set('documentKind', next);
        return params;
      },
      { replace: true },
    );

  // Narrowed on the API, which pages the queue: a filter applied to one page here would
  // miss the videos on the next.
  const queue = useModerationQueueQuery('document', {
    documentKind: kindFilter === 'all' ? undefined : kindFilter,
  });

  // The toast, the queue and the shell's badges are the hook's; closing this list's own
  // confirmation is all that is left to do here, and only once the API said yes. A
  // rejected video gets its own words: there is no credential item to delete.
  const decideMutation = useDecideModeration(
    'document',
    () => setConfirm(null),
    ({ itemId }) =>
      queue.data?.items.find((item) => item.id === itemId)?.document?.kind === 'video_intro'
        ? t('video.rejected')
        : undefined,
  );
  const talentName = (doc: ModerationDocument) => doc.candidateName || t('unknownCandidate');

  // The heading and the kind chips stay while a newly chosen kind loads; only the list
  // waits for its answer.
  return (
    <div className="space-y-6">
      <PageHeader title={t('heading')} sub={t('subheading')} />

      <FilterChips<KindFilter>
        label={t('filter.label')}
        value={kindFilter}
        onChange={setKindFilter}
        options={KIND_FILTERS.map((value) => ({ value, label: t(`filter.${value}`) }))}
      />

      <QueryGate query={queue}>
        {(data) => {
          // A document queue item always carries its document; one without is skipped.
          const rows = data.items.flatMap((item) =>
            item.document ? [{ item, doc: item.document }] : [],
          );

          const columns: ReadonlyArray<DataTableColumn<(typeof rows)[number]>> = [
            {
              key: 'item',
              header: t('table.item'),
              cell: ({ item, doc }) =>
                doc.kind === 'video_intro' ? (
                  <div className="space-y-1.5">
                    <Link
                      to={`/documents/${item.id}`}
                      state={returnState}
                      className="font-bold text-navy hover:underline"
                    >
                      {documentKindLabel(doc.kind)}
                    </Link>
                    {/* The auditor watches it here: there is no frame thumbnail to stand in. */}
                    {item.previewUrl ? (
                      <VideoPlayer
                        src={item.previewUrl}
                        label={t('video.player', { name: talentName(doc) })}
                        className="block w-56"
                      />
                    ) : (
                      <p className="text-[11.5px] text-conc-500">{t('video.noPreview')}</p>
                    )}
                  </div>
                ) : (
                  <>
                    {/* The title opens the review screen; the file itself is one more link,
                      beside it, for an admin who only wants a quick look. */}
                    <Link
                      to={`/documents/${item.id}`}
                      state={returnState}
                      className="font-bold text-navy hover:underline"
                    >
                      {doc.credentialTitle}
                    </Link>
                    <p className="flex items-center gap-2 text-[11.5px] text-conc-500">
                      {doc.credentialKind && credentialKindLabel(doc.credentialKind)}
                      {item.previewUrl && (
                        <a
                          href={item.previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-semibold text-pitch-700 hover:underline"
                        >
                          <ExternalLink aria-hidden className="h-3 w-3" />
                          {t('previewAction')}
                        </a>
                      )}
                    </p>
                  </>
                ),
            },
            {
              key: 'talent',
              header: t('column.talent'),
              cell: ({ doc }) => (
                <span className="font-semibold text-navy">{doc.candidateName}</span>
              ),
            },
            {
              key: 'issuer',
              header: t('table.issuerSize'),
              cell: ({ doc }) => (
                <>
                  {doc.kind === 'video_intro' ? (
                    // A video has no issuer; its running time stands in that column.
                    <p className="num text-conc-600">
                      {doc.durationSeconds !== undefined
                        ? formatDuration(doc.durationSeconds)
                        : t('video.noDuration')}
                    </p>
                  ) : (
                    <p className="text-conc-600">{doc.issuer ?? t('unknownIssuer')}</p>
                  )}
                  <p className="num text-[11.5px] text-conc-400">
                    {doc.filename}
                    {doc.sizeBytes !== undefined && (
                      <span className="ms-1">{formatBytes(doc.sizeBytes)}</span>
                    )}
                  </p>
                </>
              ),
            },
            {
              key: 'status',
              header: t('column.status'),
              // The moderation queue only ever holds awaiting-audit items — the payload
              // carries no per-document status — so the queue's single implicit state is
              // label-mapped rather than a real column.
              cell: () => (
                <StatusBadge tone={documentTone(DOCUMENT_STATUS.AWAITING_AUDIT)}>
                  {documentStatusLabel(DOCUMENT_STATUS.AWAITING_AUDIT)}
                </StatusBadge>
              ),
            },
            {
              key: 'actions',
              header: t('table.actions'),
              headClassName: 'text-end',
              cellClassName: 'text-end',
              cell: ({ item, doc }) => {
                const video =
                  doc.kind === 'video_intro' ? videoWording(talentName(doc)) : undefined;
                const rejectAction = video?.rejectAction ?? t('reject.action');
                return (
                  <div data-keep-click>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={() => toggleConfirm(item.id, 'approve')}
                        title={t('approve')}
                        className="px-3 py-1.5"
                      >
                        {t('approve')}
                      </Button>
                      <Button
                        variant="danger-soft"
                        size="sm"
                        onClick={() => toggleConfirm(item.id, 'reject')}
                        title={rejectAction}
                        className="px-3 py-1.5"
                      >
                        {rejectAction}
                      </Button>
                    </div>

                    {confirm?.id === item.id && (
                      <ReasonConfirm
                        key={confirm.mode}
                        subject="document"
                        mode={confirm.mode}
                        name={doc.credentialTitle ?? talentName(doc)}
                        pending={decideMutation.isPending}
                        onApprove={() =>
                          decideMutation.mutate({ itemId: item.id, decision: 'approve' })
                        }
                        onReject={(reason) =>
                          decideMutation.mutate({ itemId: item.id, decision: 'reject', reason })
                        }
                        onCancel={() => setConfirm(null)}
                        className="mt-2 text-start"
                        approveBody={video?.approveBody}
                        rejectHint={video?.rejectHint}
                      />
                    )}
                  </div>
                );
              },
            },
          ];

          return (
            <Card className="overflow-hidden shadow-sm">
              <DataTable
                caption={t('heading')}
                rows={rows}
                columns={columns}
                rowKey={({ item }) => item.id}
                onRowClick={({ item }) =>
                  void navigate(`/documents/${item.id}`, { state: returnState })
                }
                empty={
                  <EmptyState icon={FileText} title={t('empty.title')} hint={t('emptyHint')} />
                }
              />
            </Card>
          );
        }}
      </QueryGate>
    </div>
  );
}
